import {of as observableOf, Observable, Subscription, Subject, from, EMPTY} from 'rxjs';

import {mergeMap, map, first, startWith, retry, catchError, toArray} from 'rxjs/operators';
import {Component, OnDestroy} from '@angular/core';
import {ResourceService} from '../resource.service';
import {MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {LanguageService} from '../../language/language.service';
import {CacheService} from '../../cache/cache.service';
import {DialogsService} from '../../shared/dialog/dialogs.service';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {stringify} from 'csv-stringify/browser/esm';
import {DisplayAttribute} from '../displayattribute';
import {AttributeTypeDescription} from '../../cache/schema/attributetypedescription';
import {FilterResult} from '../filterresult';

const RangeValidator: ValidatorFn = (fg: UntypedFormGroup) => {
  const start = fg.get('rangeStart').value;
  const end = fg.get('rangeEnd').value;

  return start !== null && end !== null && start < end ? null : {range: true};
};

@Component({
  selector: 'csv-export-dialog',
  templateUrl: './csv-export-dialog.component.html',
  styleUrls: ['./csv-export-dialog.component.scss'],
  standalone: false,
})
export class CsvExportDialogComponent implements OnDestroy {
  reasonCaption: string = 'Reason';
  loadAll: boolean = true;

  started: boolean = false;
  loading: boolean = true;
  startIndex: number = 0;
  stopIndex: number = Number.MAX_VALUE;
  displayedLoadedCount: number = 0;
  displayedTotalCount: number = 0;

  startIndexControl: UntypedFormControl;
  stopIndexControl: UntypedFormControl;
  form: UntypedFormGroup;

  min: number = 0;
  max: number = Number.MAX_VALUE;

  delimiter: string = ';';
  referenceAttributes: Observable<DisplayAttribute[]>;
  allAttributes: Observable<AttributeTypeDescription[]>;
  defaultTargetAttribute: AttributeTypeDescription = null;
  referenceAttributesFormControls: any;
  allFilteredAttributes: any;

  headers: string[];
  objectUrl: SafeUrl;
  fileName: string;
  file: Blob = null;

  filter: string;
  filterValue: string;
  attributesearched: string[];
  attributes: string;
  sortColumn: string;
  sortAsc: boolean;
  private observableConnections: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<CsvExportDialogComponent>,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private dialogsService: DialogsService,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer,
    private languageService: LanguageService,
    private fb: UntypedFormBuilder,
    private envConfig: EnvironmentConfigService,
  ) {
    this.cacheService
      .getLanguageResource()
      .pipe(first())
      .subscribe(lr => {
        this.reasonCaption = LanguageService.translate('RequestReasonCaption', 'Reason', lr);
      });

    this.allAttributes = this.cacheService.getAttributeTypeDescriptions();
    this.allAttributes
      .subscribe(atds => {
        for (let atd of atds) {
          if (atd.systemname === this.envConfig.getAttributeName('ObjectID')) {
            this.defaultTargetAttribute = atd;
            break;
          }
        }
      })
      .unsubscribe();

    this.form = this.fb.group(
      {
        rangeStart: [this.min, Validators.required],
        rangeEnd: [this.max, Validators.required],
      },
      {validator: RangeValidator},
    );
  }

  private alive: boolean = true;

  ngOnDestroy(): void {
    this.alive = false;
    this.observableConnections.forEach((s: Subscription) => s.unsubscribe());
  }

  public init(
    headers: string[],
    filter: string,
    filterValue: string,
    attributesearched: string[],
    attributes: string,
    objectType: string,
    totalCount: number,
    sortColumn = this.envConfig.getAttributeName('DisplayName'),
    sortAsc: 'asc' | 'desc' = 'asc',
    fileName = 'data.csv',
  ): void {
    this.headers = headers;
    this.filter = filter;
    this.filterValue = filterValue;
    this.attributesearched = attributesearched;
    this.attributes = attributes;
    this.sortColumn = sortColumn;
    this.sortAsc = sortAsc === 'asc';
    if (!fileName.endsWith('.csv')) {
      fileName = fileName + '.csv';
    }
    this.fileName = fileName;
    this.displayedTotalCount = totalCount;
    this.stopIndex = totalCount;
    this.max = totalCount;

    const validators: ValidatorFn[] = [];
    validators.push(Validators.required);
    validators.push(Validators.min(this.min));
    validators.push(Validators.max(this.max));

    this.form.get('rangeStart').setValidators(validators);
    this.form.get('rangeEnd').setValidators(validators);
    this.form.get('rangeEnd').setValue(this.max);
    this.form.updateValueAndValidity();

    // detect references
    this.referenceAttributesFormControls = {};
    this.allFilteredAttributes = {};
    this.referenceAttributes = this.cacheService
      .getDisplayAttributes(this.headers, objectType)
      .pipe(map(list => list.filter(da => da.DataType === 'Reference')));
  }

  getFormControlForReferenceAttribute(attributeName: string): FormControl {
    if (
      !Object.prototype.hasOwnProperty.call(this.referenceAttributesFormControls, attributeName)
    ) {
      this.referenceAttributesFormControls[attributeName] = new FormControl(
        this.defaultTargetAttribute,
      );
    }
    return this.referenceAttributesFormControls[attributeName];
  }

  displayAttributeFilterValue(obj): string {
    return obj && obj.displayname ? obj.displayname : '';
  }

  getAllFilteredAttributesForReferenceAttribute(
    attributeName: string,
  ): Observable<AttributeTypeDescription[]> {
    if (!Object.prototype.hasOwnProperty.call(this.allFilteredAttributes, attributeName)) {
      const control = this.getFormControlForReferenceAttribute(attributeName);
      this.allFilteredAttributes[attributeName] = control.valueChanges.pipe(
        startWith(''),
        mergeMap(filterValue => {
          filterValue = typeof filterValue === 'string' ? filterValue : filterValue?.displayname;
          filterValue = filterValue || '';
          filterValue = filterValue.toLowerCase();
          return this.allAttributes.pipe(
            map(all =>
              all.filter(
                atd =>
                  atd.displayname.toLowerCase().includes(filterValue) ||
                  atd.systemname.toLowerCase().includes(filterValue),
              ),
            ),
          );
        }),
      );
    }
    return this.allFilteredAttributes[attributeName];
  }

  submit(): void {
    if (this.file == null) {
      this.objectUrl = null;
    }
    this.dialogRef.close();
  }

  startDownload() {
    this.form.updateValueAndValidity();
    if (this.form.invalid && !this.loadAll) {
      return;
    }
    this.started = true;
    if (!this.loadAll) {
      this.startIndex = this.form.get('rangeStart').value;
      this.stopIndex = this.form.get('rangeEnd').value;
    }
    this.displayedTotalCount = this.stopIndex - this.startIndex;

    this.observableConnections.push(
      this.fetchPage(this.startIndex)
        .pipe(
          mergeMap(res => this.replaceReferencesInModel(res)),
          mergeMap(res => this.parsePageToCSV(res)),
        )
        .subscribe({
          next: res => {
            this.file = new Blob([res], {type: 'text/csv;charset=utf8'});
            const url = URL.createObjectURL(this.file);
            this.objectUrl = this.sanitizer.bypassSecurityTrustUrl(url);
            this.loading = false;
          },
          error: err => {
            this.handleError(err);
            this.loading = false;
            this.started = false;
          },
        }),
    );
  }

  displayInputError(key: string): Observable<string> {
    return this.cacheService
      .getLanguageResource()
      .pipe(map(lr => LanguageService.translate(key, 'Invalid Input', lr)));
  }

  replaceReferencesInModel(model: any[]): Observable<any[]> {
    return this.referenceAttributes.pipe(
      mergeMap(refAttrs => {
        const attributesToQuery = new Set();
        const idsToQuery = new Set();
        const attributesToChange = {};

        for (let da of refAttrs) {
          const fcValue = this.getFormControlForReferenceAttribute(da.Name).value;
          if (
            typeof fcValue === 'object' &&
            fcValue != null &&
            'systemname' in fcValue &&
            fcValue.systemname !== this.envConfig.getAttributeName('ObjectID')
          ) {
            attributesToChange[da.Name] = fcValue.systemname;
            attributesToQuery.add(fcValue.systemname);
            model
              .map(x => x[da.Name])
              .filter(x => x != null && x !== '')
              .forEach(x => {
                if (Array.isArray(x)) {
                  x.forEach(y => idsToQuery.add(y));
                } else {
                  idsToQuery.add(x);
                }
              });
          }
        }

        if (attributesToQuery.size === 0 || idsToQuery.size === 0) {
          return observableOf(model);
        }

        const idsPerStep = 25;
        const attributesToQueryStr = [...attributesToQuery].join(',');
        const idsToLookUp = [...idsToQuery];

        const stepObservables = [];
        for (let i = 0; i < Math.ceil(idsToLookUp.length / idsPerStep); i++) {
          const stepFilter =
            '/*[' +
            idsToLookUp
              .slice(i * idsPerStep, (i + 1) * idsPerStep)
              .map(x => this.envConfig.getAttributeName('ObjectID') + '="' + x + '"')
              .join(' or ') +
            ']';
          stepObservables.push(
            this.resourceService.searchResources(stepFilter, attributesToQueryStr, idsPerStep),
          );
        }

        return from(stepObservables).pipe(
          mergeMap(
            obs =>
              obs.pipe(
                retry(2),
                catchError(() => EMPTY),
              ),
            3,
          ),
          toArray(),
          map((frs: FilterResult[]) => {
            if (frs == null || frs.length === 0) return model;

            const dictionary = {};
            frs
              .filter(fr => fr != null && fr.Results.length > 0)
              .map(frs => frs.Results)
              .flat()
              .forEach(x => (dictionary[x[this.envConfig.getAttributeName('ObjectID')]] = x));

            return model.map(entry => {
              Object.keys(attributesToChange).forEach(sourceAttribute => {
                const targetAttribute = attributesToChange[sourceAttribute];
                if (
                  targetAttribute != null &&
                  entry[sourceAttribute] != null &&
                  entry[sourceAttribute] !== ''
                ) {
                  if (Array.isArray(entry[sourceAttribute])) {
                    entry[sourceAttribute] = entry[sourceAttribute].map(entryElem => {
                      if (entryElem in dictionary && targetAttribute in dictionary[entryElem]) {
                        return dictionary[entryElem][targetAttribute];
                      } else {
                        return entryElem;
                      }
                    });
                  } else {
                    if (
                      entry[sourceAttribute] in dictionary &&
                      targetAttribute in dictionary[entry[sourceAttribute]]
                    ) {
                      entry[sourceAttribute] = dictionary[entry[sourceAttribute]][targetAttribute];
                    }
                  }
                }
              });
              return entry;
            });
          }),
        );
      }),
    );
  }

  parsePageToCSV(model: any[]): Observable<string> {
    const subject = new Subject<string>();
    const stringifier = stringify({
      delimiter: this.delimiter ?? ',',
      bom: true, // important for Excel
      cast: {
        object: function (value) {
          if (Array.isArray(value)) {
            return value.join(',');
          } else {
            return JSON.stringify(value);
          }
        },
      },
    });

    const csvData = [];

    let row;
    stringifier.on('readable', function () {
      while ((row = stringifier.read()) !== null) {
        csvData.push(row);
      }
    });

    stringifier.on('error', err => this.handleError(err.message));

    stringifier.on('finish', function () {
      subject.next(csvData.join(''));
      subject.complete();
    });

    // Headers
    stringifier.write(this.headers);

    // Data rows
    for (let entry of model) {
      stringifier.write(this.headers.map(h => entry[h]));
    }

    stringifier.end();

    return subject;
  }

  fetchPage(index: number, pageSize = 1000): Observable<any[]> {
    if (index == this.stopIndex) {
      return observableOf([]);
    }
    pageSize = index + pageSize > this.stopIndex ? this.stopIndex - index : pageSize;
    return this.resourceService
      .searchAndFilterResources(
        this.filter,
        this.filterValue,
        this.attributesearched,
        this.attributes,
        pageSize,
        index,
        this.envConfig.getAttributeName('DisplayName'),
        true,
      )
      .pipe(
        first(),
        mergeMap(res => {
          if (res != null) {
            if (index === this.startIndex && res.Limitation != null) {
              // the search had to be limited to keep the requests below the query string
              // budget; export what the limited search returns and tell the user once
              this.cacheService
                .getLanguageResource()
                .pipe(first())
                .subscribe(lr => {
                  const message = ResourceService.searchLimitationMessage(res.Limitation, lr);
                  if (message != null) {
                    this.snackBar.open(message, null, {duration: 8000});
                  }
                });
            }
            this.displayedLoadedCount += res.Results.length;
            if (res.HasMoreItems && res.Results.length > 0) {
              return this.fetchPage(index + pageSize).pipe(
                map(addRes => {
                  if (addRes != null) {
                    return res.Results.concat(addRes);
                  }
                }),
              );
            }
            return observableOf(res.Results);
          }
          return observableOf(null);
        }),
      );
  }

  formatNumber(number: Number, options?: Intl.NumberFormatOptions): Observable<string> {
    return this.languageService
      .getLanguage(this.cacheService.getSupportedLocaleConfiguration())
      .pipe(
        map((language: string) => {
          return number.toLocaleString(language, options);
        }),
      );
  }

  handleError(error: any, caught?: Observable<any>) {
    if (this.dialogsService != null && this.cacheService != null) {
      this.cacheService
        .getLanguageResource()
        .pipe(first())
        .subscribe(lr => {
          const title = LanguageService.translate('ResourceDetailErrorTitle', 'Error', lr);
          let msg = LanguageService.translate(
            'ResourceDetailErrorMessage',
            'There occurred an error.',
            lr,
          );
          this.dialogsService.webserviceError(error, title, msg, lr);
        });
    }
    return observableOf(null);
  }

  onLoadAll(loadAll: boolean) {
    this.loadAll = loadAll;
    if (!loadAll) {
      this.form.get('rangeStart').setValue(this.min);
      this.form.get('rangeEnd').setValue(this.max);
    }
    this.form.updateValueAndValidity();
  }
}
